import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@/lib/zodcompat";
import { z } from "zod";
import { Dialog } from "@base-ui/react/dialog";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useReceiveFreight } from "@/hooks/useFreights";
import { useCashAccounts } from "@/hooks/useCashAccounts";
import { formatBRL } from "@/lib/currency";
import { cn } from "@/lib/utils";

/**
 * Mínimo que o modal precisa do frete. Recebe `Freight` completo (do detalhe/lista)
 * ou um shape reduzido (do dashboard, evitando fetch adicional).
 */
export interface ReceiveTarget {
  readonly id: string;
  readonly code: number;
  readonly freightValue: string | number | null;
  readonly company: { tradeName: string };
}

const PAYMENT_METHODS = [
  { value: "PIX", label: "PIX" },
  { value: "BANK_TRANSFER", label: "Transferência bancária" },
  { value: "BANK_SLIP", label: "Boleto" },
  { value: "CASH", label: "Dinheiro" },
  { value: "OTHER", label: "Outro" },
] as const;

const selectClass = cn(
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

const schema = z.object({
  paymentDate: z.string().min(1, "Data obrigatória"),
  method: z.enum(["PIX", "BANK_TRANSFER", "BANK_SLIP", "CASH", "OTHER"]),
  accountId: z.string().min(1, "Conta obrigatória"),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface FreightReceiveModalProps {
  readonly freight: ReceiveTarget;
  readonly open: boolean;
  readonly onClose: () => void;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function apiError(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  );
}

export function FreightReceiveModal({ freight, open, onClose }: FreightReceiveModalProps) {
  const { mutateAsync, isPending } = useReceiveFreight(freight.id);
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 });
  const accounts = accountsData?.data ?? [];

  const amount = Number(freight.freightValue);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      paymentDate: todayISO(),
      method: "PIX",
      accountId: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({ paymentDate: todayISO(), method: "PIX", accountId: "", notes: "" });
    }
  }, [open, reset]);

  useEffect(() => {
    if (open && accounts.length > 0) {
      const fallback = accounts.find((a) => a.isDefault)?.id ?? accounts[0]!.id;
      setValue("accountId", fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountsData]);

  async function onSubmit(data: FormData) {
    try {
      await mutateAsync({
        paymentDate: new Date(data.paymentDate).toISOString(),
        method: data.method,
        accountId: data.accountId,
        notes: data.notes || undefined,
      });
      toast.success(`Frete F-${freight.code} marcado como recebido`);
      onClose();
    } catch (err) {
      toast.error(apiError(err, "Erro ao registrar recebimento"));
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Receber pagamento
          </Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            Frete F-{freight.code} · {freight.company.tradeName}
          </p>

          <div className="mt-4 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <p className="text-xs text-muted-foreground">Valor do frete</p>
            <p className="text-xl font-semibold text-green-700 tabular-nums">
              + {formatBRL(amount)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Uma fatura F-{freight.code} será criada automaticamente.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="paymentDate">Data do pagamento *</Label>
                <Input
                  id="paymentDate"
                  type="date"
                  aria-invalid={!!errors.paymentDate}
                  {...register("paymentDate")}
                />
                {errors.paymentDate && (
                  <p className="text-xs text-destructive">{errors.paymentDate.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="method">Forma *</Label>
                <Controller
                  name="method"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="method"
                      value={field.value}
                      onChange={field.onChange}
                      className={selectClass}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="accountId">Conta de recebimento *</Label>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <select
                    id="accountId"
                    value={field.value}
                    onChange={field.onChange}
                    className={selectClass}
                  >
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.accountId && (
                <p className="text-xs text-destructive">{errors.accountId.message}</p>
              )}
              {accounts.length === 0 && (
                <p className="text-xs text-destructive">
                  Cadastre uma conta de caixa antes de receber pagamentos.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Observações</Label>
              <Input id="notes" placeholder="Opcional" {...register("notes")} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                loading={isPending}
                loadingText="Registrando…"
                disabled={accounts.length === 0}
              >
                <CheckCircle2 size={16} />
                Confirmar recebimento
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
